import { useEffect, useMemo, useState, useRef } from "react";
import { LegacyStack, Link, Select, Text } from "@shopify/polaris";
import { getThemeSelectOptions } from "@vendor-app/app/AdminRoute/AdminSetupRoute/libs/utils/getThemeSelectOptions";
import { GettingStartedStoreAccess } from "../../../libs/store";
import { UnsupportedThemeBanner } from "../../InstallPortal/UnsupportedThemeBanner";
import { useCheckIfThemeIsValid } from "../../../libs/hooks/useCheckIfThemeIsValid";
import useValidateThemeFile from "../../../libs/hooks/useValidateThemeFile";

export const ThemeSelect = () => {
  const { setChosenThemeId, setDisplayVerifyImplementationText, update } = GettingStartedStoreAccess.useActions();
  const chosenThemeId = GettingStartedStoreAccess.useChosenThemeId();
  const isLoading = GettingStartedStoreAccess.useLoading("subscriptionBlock");
  const themes = GettingStartedStoreAccess.useThemes();
  const { isThemeValid, isProductFileValid } = GettingStartedStoreAccess.useThemeValidation();

  const { checkIfThemeIsValid } = useCheckIfThemeIsValid();
  const validateThemeFile = useValidateThemeFile();

  const [isValidatingTheme, setIsValidatingTheme] = useState(false);

  const options = useMemo(() => getThemeSelectOptions(chosenThemeId, themes), [chosenThemeId, themes]);

  const selectContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    selectContainerRef.current?.querySelector("select")?.setAttribute("data-testid", "app-block-theme-select");
  }, []);

  useEffect(() => {
    if (!chosenThemeId) {
      return;
    }

    setIsValidatingTheme(true);

    void Promise.all([
      checkIfThemeIsValid(chosenThemeId),
      validateThemeFile(chosenThemeId, "templates/product.json"),
    ]).finally(() => {
      setIsValidatingTheme(false);
    });
  }, [chosenThemeId]);

  const onChange = (themeId: string) => {
    setChosenThemeId(themeId);
    setDisplayVerifyImplementationText(false);

    update(
      [
        {
          path: "onSiteSetup.smartrrWidget.verified",
          val: false,
        },
        {
          path: "onSiteSetup.smartrrWidget.completed",
          val: false,
        },
      ],
      "subscriptionBlock"
    );
  };

  const getHelpText = () => {
    if (isLoading || isValidatingTheme || !chosenThemeId || !isThemeValid) {
      return;
    }

    if (isProductFileValid) {
      return (
        <Text as="span" variant="bodySm">
          Recommended location: place the Smartrr block right above the Buy buttons block in the Product
          information section. We&apos;ll remember this theme for the rest of your setup.
        </Text>
      );
    }

    return (
      <Text as="span" variant="bodySm" color="critical">
        We couldn&apos;t find the Buy buttons block in this theme&apos;s Default product template. The editor will
        open on the Default product template previewing a subscription product — see the{" "}
        <Link url="https://help.smartrr.com/docs/getting-started/implementation/installing-smartrr-on-a-shopify-2.0-theme-automatic-install">
          help doc
        </Link>{" "}
        for where to place the block.
      </Text>
    );
  };

  return (
    <LegacyStack vertical distribution="fill" alignment="fill">
      <div ref={selectContainerRef}>
        <Select
          label="Theme"
          helpText={getHelpText()}
          value={chosenThemeId}
          options={options}
          onChange={onChange}
          disabled={isLoading}
        />
      </div>
      <UnsupportedThemeBanner />
    </LegacyStack>
  );
};
